import axiosRetry, { type IAxiosRetryConfig } from "axios-retry";
import { AxiosError, type AxiosInstance, type AxiosRequestConfig } from "axios";

/** HTTP 重试策略配置。 */
export type HttpRetryPolicy = {
  retries: number;
  delayBaseMs: number;
  maxDelayMs: number;
  shouldResetTimeout: boolean;
};

/** 默认 HTTP 重试策略。 */
export const DEFAULT_HTTP_RETRY_POLICY: HttpRetryPolicy = {
  delayBaseMs: 300,
  maxDelayMs: 5_000,
  retries: 2,
  shouldResetTimeout: true,
};

const IDEMPOTENT_METHODS = new Set(["delete", "get", "head", "options", "put"]);

/** 根据状态码判断是否允许重试。 */
export function shouldRetry(statusCode: number): boolean {
  return statusCode === 408 || statusCode === 425 || statusCode === 429 || statusCode >= 500;
}

/** 判断 axios 错误是否满足统一重试条件。 */
export function shouldRetryError(error: AxiosError): boolean {
  const requestConfig = error.config;
  if (isRetryDisabledByRequest(requestConfig)) {
    return false;
  }

  if (error.code === AxiosError.ECONNABORTED || error.code === AxiosError.ERR_NETWORK) {
    return true;
  }

  const statusCode = error.response?.status;
  if (typeof statusCode !== "number") {
    return false;
  }

  if (!shouldRetry(statusCode)) {
    return false;
  }

  const method = (requestConfig?.method ?? "get").toLowerCase();
  return IDEMPOTENT_METHODS.has(method);
}

/** 计算指数退避时间（含轻量抖动）。 */
export function calculateRetryDelay(
  retryCount: number,
  policy: HttpRetryPolicy,
  randomFactor: number = Math.random(),
): number {
  const exponentialDelay = policy.delayBaseMs * 2 ** (retryCount - 1);
  const jitter = Math.floor(exponentialDelay * 0.2 * randomFactor);
  return Math.min(policy.maxDelayMs, exponentialDelay + jitter);
}

/** 将重试策略注册到 axios 客户端。 */
export function applyRetryPolicy(
  client: AxiosInstance,
  policyOverrides: Partial<HttpRetryPolicy> = {},
): HttpRetryPolicy {
  const policy: HttpRetryPolicy = {
    ...DEFAULT_HTTP_RETRY_POLICY,
    ...policyOverrides,
  };

  const retryConfig: IAxiosRetryConfig = {
    retries: policy.retries,
    retryCondition: shouldRetryError,
    retryDelay: (retryCount) => calculateRetryDelay(retryCount, policy),
    shouldResetTimeout: policy.shouldResetTimeout,
  };
  axiosRetry(client, retryConfig);
  return policy;
}

/** 判断当前请求是否显式关闭自动重试。 */
function isRetryDisabledByRequest(requestConfig?: AxiosRequestConfig): boolean {
  if (!requestConfig) {
    return false;
  }

  const retryConfig = requestConfig["axios-retry"];
  if (!retryConfig || typeof retryConfig !== "object") {
    return false;
  }

  const retries = Reflect.get(retryConfig, "retries");
  return typeof retries === "number" && retries <= 0;
}

